import {patch} from '@rails/request.js'

/**
 * CheckoutService - Handles all backend API calls for checkout
 *
 * This service encapsulates all HTTP interactions with the checkout backend,
 * providing a clean API for controllers to use.
 *
 * Uses @rails/request.js for automatic:
 * - CSRF token handling
 * - Turbo Stream response processing
 * - Rails conventions
 */
export class CheckoutService {
    /**
     * Save contact information to backend
     * @param {string} email - User email
     * @returns {Promise<{success: boolean, errors?: object}>}
     */
    async saveContact(email) {
        return this._submitToEndpoint('/checkout/update_contact', {email});
    }

    /**
     * Save shipping/billing information
     * @param {object} shippingData - Shipping and billing fields
     * @returns {Promise<{success: boolean, errors?: object}>}
     */
    async saveShipping(shippingData) {
        return this._submitToEndpoint('/checkout/update_shipping', shippingData);
    }

    /**
     * Refresh order summary frame. Resolves on turbo:frame-load (the endpoint
     * renders a matching turbo-frame specifically so this event fires); the
     * timeout is only a backstop so a failed fetch can never gate the
     * section transition.
     */
    refreshOrderSummary() {
        try {
            const frame = document.getElementById('order_summary');
            if (!frame) return Promise.resolve();

            return new Promise((resolve) => {
                const done = () => resolve();
                frame.addEventListener('turbo:frame-load', done, {once: true});
                setTimeout(done, 1500);
                if (frame.src) {
                    // Re-assigning an identical src is a no-op on a completed
                    // frame; reload() forces the re-fetch.
                    frame.reload();
                } else {
                    frame.src = '/checkout/refresh_summary';
                }
            });
        } catch (error) {
            console.error('Error refreshing order summary:', error);
        }
        return Promise.resolve();
    }

    /**
     * Generic method to submit data to an endpoint using @rails/request.js
     *
     * @param {string} url - The endpoint to submit to
     * @param {object} data - The data object to send (will be wrapped in { order: data })
     * @returns {Promise<{success: boolean, errors?: object}>}
     * @private
     */
    async _submitToEndpoint(url, data) {
        try {
            const response = await patch(url, {
                body: JSON.stringify({order: data}),
                contentType: 'application/json',
                responseKind: 'json'
            });

            if (response.ok) {
                const result = await response.json;
                return {
                    success: result.success,
                    errors: result.errors || null,
                    message: result.message || null,
                    totalAmount: result.total_amount || null,
                    availablePaymentTypes: result.available_payment_types || []
                };
            } else {
                const error = await response.json;
                console.error('Server error:', error);
                return {
                    success: false,
                    errors: error.errors || {server: 'Server error'},
                    message: error.message || error.error || 'Server error'
                };
            }
        } catch (error) {
            console.error('Network error:', error);
            return {
                success: false,
                errors: {network: 'Connection failed. Please try again.'}
            };
        }
    }
}
